import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  startTransition,
} from "react";
import { supabase } from "lib/supabaseClient";
import Widget from "components/widget/Widget";
import Card from "components/card";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import "flatpickr/dist/themes/dark.css";
import {
  MdAssignmentInd,
  MdFingerprint,
  MdDateRange,
  MdContactPhone,
  MdHistory,
  MdEmail,
  MdWork,
  MdAccountTree,
  MdAccessTime,
  MdNotifications,
  MdFileUpload,
  MdDescription,
  MdInfo,
  MdDelete,
  MdCheckCircle,
  MdCancel,
  MdTerminal,
  MdAttachment,
  MdOpenInNew,
  MdVerifiedUser,
  MdTimer,
  MdDeleteSweep,
  MdErrorOutline,
  MdCalendarToday,
} from "react-icons/md";

// ============ Fixed Components for Performance ============
const InfoField = React.memo(({ icon, label, value }) => (
  <div className="group rounded-xl border border-gray-200 bg-gray-50 p-4 text-left transition-all hover:border-core-accent/30">
    <div className="mb-1 flex items-center gap-2">
      <span className="text-sm text-core-accent transition-transform group-hover:scale-110">
        {icon}
      </span>
      <p className="font-mono text-[8px] font-medium uppercase tracking-tighter text-gray-500">
        {label}
      </p>
    </div>
    <p className="truncate font-mono text-[12px] font-medium uppercase text-core-charcoal">
      {value || "---"}
    </p>
  </div>
));

const BalanceRow = React.memo(({ label, used, total, remaining, color }) => (
  <div className="flex items-center justify-between border-b border-gray-200 py-2">
    <span className="flex items-center gap-2 text-gray-500">
      <span
        className="h-2 w-2 rounded-full"
        style={{ backgroundColor: color }}
      ></span>
      <span className="text-[10px] font-medium uppercase">{label}</span>
    </span>
    <div className="font-mono text-[11px]">
      <span className="font-medium text-core-charcoal">{used}D</span>
      {total !== undefined && (
        <span className="text-gray-400"> / {total}D</span>
      )}
      {remaining !== undefined && (
        <span className="ml-1 text-gray-400">({remaining}D)</span>
      )}
    </div>
  </div>
));

const getAvatarCacheKey = (url) =>
  url ? `core_avatar_cache_${btoa(url).slice(0, 120)}` : null;

const readCachedAvatar = (url) => {
  const key = getAvatarCacheKey(url);
  if (!key) return null;
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeCachedAvatar = (url, dataUrl) => {
  const key = getAvatarCacheKey(url);
  if (!key || !dataUrl) return;
  try {
    localStorage.setItem(key, dataUrl);
  } catch {
    try {
      sessionStorage.setItem(key, dataUrl);
    } catch {}
  }
};

const blobToDataUrl = (blob) =>
  new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result || null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    } catch {
      resolve(null);
    }
  });

const fetchAvatarBlob = async (url) => {
  if (!url) return null;
  try {
    if (url.includes("supabase.co/storage/v1/object/public/")) {
      const path = url.split("/public/")[1];
      const bucket = path.split("/")[0];
      const filePath = path.split("/").slice(1).join("/");
      const { data, error } = await supabase.storage
        .from(bucket)
        .download(filePath);
      if (!error && data) return data;
    }
    const response = await fetch(url, { cache: "force-cache" });
    return await response.blob();
  } catch {
    return null;
  }
};

const SafeAvatar = React.memo(({ url, wrapperClassName, imageClassName }) => {
  const [cachedUrl, setCachedUrl] = useState(() => readCachedAvatar(url));
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    setCachedUrl(readCachedAvatar(url));
  }, [url]);

  useEffect(() => {
    let isActive = true;
    let nextObjectUrl;
    const load = async () => {
      if (!url) {
        setObjectUrl(null);
        return;
      }
      const blob = await fetchAvatarBlob(url);
      if (!isActive) return;
      if (blob) {
        nextObjectUrl = URL.createObjectURL(blob);
        setObjectUrl(nextObjectUrl);
        const dataUrl = await blobToDataUrl(blob);
        if (dataUrl && isActive) {
          writeCachedAvatar(url, dataUrl);
          setCachedUrl(dataUrl);
        }
      }
    };
    load();
    return () => {
      isActive = false;
      if (nextObjectUrl) URL.revokeObjectURL(nextObjectUrl);
    };
  }, [url]);

  const displayUrl =
    objectUrl || cachedUrl || "https://i.ibb.co/mRpg6P0/default-avatar.png";
  return (
    <div
      className={`relative select-none ${wrapperClassName}`}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="absolute inset-0 z-10 bg-transparent" />
      <div
        className={imageClassName}
        style={{
          backgroundImage: `url(${displayUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          pointerEvents: "none",
        }}
      />
    </div>
  );
});

const Dashboard = () => {
  const [userId, setUserId] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const isMounted = useRef(true);
  const channelRef = useRef(null);
  const refreshingRef = useRef(false);

  const [data, setData] = useState(() => {
    try {
      const ssc = sessionStorage.getItem("core_dashboard_v6");
      if (ssc) return JSON.parse(ssc);
      const lsc = localStorage.getItem("core_dashboard_v6");
      if (lsc) return JSON.parse(lsc);
      const profS = sessionStorage.getItem("core_admin_user_profile");
      const profL = localStorage.getItem("core_admin_user_profile");
      const profile = profS ? JSON.parse(profS) : profL ? JSON.parse(profL) : null;
      const staffS = sessionStorage.getItem("core_workforce_employees");
      const staffL = localStorage.getItem("core_workforce_employees");
      const staff = staffS ? JSON.parse(staffS) : staffL ? JSON.parse(staffL) : null;
      if (profile || staff) {
        const totalStaff = Array.isArray(staff) ? staff.length : 0;
        const payroll = Array.isArray(staff)
          ? staff.reduce((sum, emp) => sum + (Number(emp.basic_salary) || 0), 0)
          : 0;
        return {
          profile,
          leaves: [],
          totalStaff,
          payroll,
          unread: 0,
          activityLogs: [],
        };
      }
      return null;
    } catch {
      return null;
    }
  });

  const [loading, setLoading] = useState(!data);
  const [submitting, setSubmitting] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [leaveRequest, setLeaveRequest] = useState({
    leaveType: "Annual Leave",
    reason: "",
    startDate: null,
    endDate: null,
    file: null,
  });

  const cardStyle = "bg-white border border-gray-200 shadow-sm";
  const inputStyle =
    "w-full bg-gray-50 border border-gray-200 p-3 rounded-lg text-core-charcoal text-xs outline-none focus:border-core-accent transition-all font-mono tracking-wider";

  const leaveTypeColors = useMemo(
    () => ({
      "Annual Leave": {
        text: "text-blue-400",
        dot: "#3b82f6",
        bg: "bg-blue-500/10",
      },
      "Sick Leave": {
        text: "text-purple-400",
        dot: "#8b5cf6",
        bg: "bg-purple-500/10",
      },
      "Emergency Leave": {
        text: "text-orange-400",
        dot: "#f97316",
        bg: "bg-orange-500/10",
      },
    }),
    []
  );

  const toSQLDate = useCallback((date) => {
    if (!date) return null;
    const d = new Date(date);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().split("T")[0];
  }, []);

  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return "00/00/0000";
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? "00/00/0000" : d.toLocaleDateString("en-GB");
  }, []);

  const prefetchModules = useCallback(async () => {
    try {
      const [staffRes, reqRes, crmRes, artRes, logsEmpRes, logsSecRes] =
        await Promise.all([
          supabase
            .from("employees")
            .select(
              "id, full_name, employee_number, job_title, email, phone_number, avatar_url, status, basic_salary"
            )
            .order("employee_number")
            .limit(200),
          supabase
            .from("employee_leaves")
            .select("*, employee:employees(id, full_name, employee_number)")
            .order("created_at", { ascending: false })
            .limit(100),
          supabase
            .from("leads")
            .select("id, client_name, status, created_at")
            .order("created_at", { ascending: false })
            .limit(100),
          supabase
            .from("articles")
            .select("id, title, category, image_url, is_published, created_at")
            .order("created_at", { ascending: false })
            .limit(100),
          supabase
            .from("employee_logs")
            .select("*")
            .order("created_at", { ascending: false })
            .limit(100),
          supabase
            .from("security_logs")
            .select("*")
            .order("timestamp", { ascending: false })
            .limit(100),
        ]);
      const mergedLogs = [
        ...(logsEmpRes.data || []),
        ...(logsSecRes.data || []),
      ];
      try {
        sessionStorage.setItem(
          "core_workforce_employees",
          JSON.stringify(staffRes.data || [])
        );
        sessionStorage.setItem(
          "core_requests_cache",
          JSON.stringify(reqRes.data || [])
        );
        localStorage.setItem(
          "core_articles_cache",
          JSON.stringify(artRes.data || [])
        );
        sessionStorage.setItem("core_logs_cache", JSON.stringify(mergedLogs));
        localStorage.setItem("core_logs_cache", JSON.stringify(mergedLogs));
        const crmPayload = {
          data: crmRes.data || [],
          ts: Date.now(),
          key: "all|",
        };
        localStorage.setItem(
          "crm_leads_cache_local",
          JSON.stringify(crmPayload)
        );
      } catch {}
    } catch {}
  }, []);

  const refreshData = useCallback(
    async (uid) => {
      if (!uid || !isMounted.current) return;
      if (refreshingRef.current) return;
      refreshingRef.current = true;
      try {
        const profilePromise = supabase
          .from("employees")
          .select("*")
          .eq("id", uid)
          .single();
        const leavesPromise = supabase
          .from("employee_leaves")
          .select("*")
          .eq("employee_id", uid)
          .order("created_at", { ascending: false })
          .limit(50);
        const employeesPromise = isAdmin
          ? supabase.from("employees").select("basic_salary")
          : Promise.resolve({ data: null });
        const notifsPromise = supabase
          .from("notifications")
          .select("id")
          .eq("recipient_id", uid)
          .eq("is_read", false);
        const logsPromise = supabase
          .from("employee_logs")
          .select("*")
          .eq("employee_id", uid)
          .order("created_at", { ascending: false })
          .limit(20);
        const profile = await profilePromise;
        if (isMounted.current) {
          startTransition(() =>
            setData((prev) => ({
              ...(prev || {}),
              profile: profile.data || prev?.profile || null,
            }))
          );
          setLoading(false);
          try {
            sessionStorage.setItem(
              "core_admin_user_profile",
              JSON.stringify(profile.data || null)
            );
            localStorage.setItem(
              "core_admin_user_profile",
              JSON.stringify(profile.data || null)
            );
          } catch {}
        }
        const [leaves, employees, notifs, logs] = await Promise.all([
          leavesPromise,
          employeesPromise,
          notifsPromise,
          logsPromise,
        ]);

        const freshData = {
          profile: profile.data,
          leaves: leaves.data || [],
          totalStaff: employees.data?.length || 0,
          payroll:
            employees.data?.reduce(
              (sum, emp) => sum + (Number(emp.basic_salary) || 0),
              0
            ) || 0,
          unread: notifs.data?.length || 0,
          activityLogs: logs.data || [],
        };

        if (isMounted.current) {
          startTransition(() => setData(freshData));
          try {
            sessionStorage.setItem(
              "core_dashboard_v6",
              JSON.stringify(freshData)
            );
            localStorage.setItem(
              "core_dashboard_v6",
              JSON.stringify(freshData)
            );
          } catch {}
          setLoading(false);
          setTimeout(() => {
            prefetchModules().catch(() => {});
          }, 350);
        }
      } catch (err) {
        console.error("Data Error:", err);
        setLoading(false);
      }
      finally {
        refreshingRef.current = false;
      }
    },
    [isAdmin, prefetchModules]
  );
  useEffect(() => {
    isMounted.current = true;
    const init = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user && isMounted.current) {
        setUserId(user.id);
        try {
          const { data: profileRow } = await supabase
            .from("employees")
            .select("role")
            .eq("id", user.id)
            .single();
          setIsAdmin(
            profileRow?.role === "admin" ||
              user.id === "6f226d92-8217-415b-991a-69da541ed483"
          );
        } catch {
          setIsAdmin(user.id === "6f226d92-8217-415b-991a-69da541ed483");
        }
        await refreshData(user.id);

        if (channelRef.current) supabase.removeChannel(channelRef.current);
        channelRef.current = supabase
          .channel(`dash-${user.id}`)
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "employee_leaves",
              filter: `employee_id=eq.${user.id}`,
            },
            () => refreshData(user.id)
          )
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "employee_logs",
              filter: `employee_id=eq.${user.id}`,
            },
            () => refreshData(user.id)
          )
          .on(
            "postgres_changes",
            {
              event: "UPDATE",
              schema: "public",
              table: "employees",
              filter: `id=eq.${user.id}`,
            },
            (payload) => {
              const newRole = payload?.new?.role;
              setIsAdmin(
                newRole === "admin" ||
                  user.id === "6f226d92-8217-415b-991a-69da541ed483"
              );
              refreshData(user.id);
            }
          )
          .subscribe();
      }
    };
    init();
    return () => {
      isMounted.current = false;
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [refreshData]);

  const currentStatusInfo = useMemo(() => {
    if (!data?.profile) return { status: "OFFLINE", bg: "#6b7280" };
    const today = toSQLDate(new Date());
    const activeLeave = data.leaves?.find(
      (l) =>
        l.status === "approved" && today >= l.start_date && today <= l.end_date
    );
    if (activeLeave) {
      let color = "#3b82f6";
      if (activeLeave.leave_type === "Sick Leave") color = "#8b5cf6";
      if (activeLeave.leave_type === "Emergency Leave") color = "#f97316";
      const ret = new Date(
        new Date(activeLeave.end_date).setDate(
          new Date(activeLeave.end_date).getDate() + 1
        )
      );
      return {
        status: activeLeave.leave_type.toUpperCase(),
        isOnLeave: true,
        returnDate: ret,
        bg: color,
        leaveId: activeLeave.id,
      };
    }
    return { status: "ACTIVE", isOnLeave: false, bg: "#10b981" };
  }, [data, toSQLDate]);

  const upcomingStatusInfo = useMemo(() => {
    if (!data?.leaves || currentStatusInfo.isOnLeave) return null;
    const todayStr = toSQLDate(new Date());
    const futureApproved = (data.leaves || []).filter(
      (l) => l.status === "approved" && l.start_date > todayStr
    );
    if (!futureApproved.length) return null;
    futureApproved.sort(
      (a, b) => new Date(a.start_date) - new Date(b.start_date)
    );
    const nextLeave = futureApproved[0];
    let color = "#14b8a6";
    if (nextLeave.leave_type === "Sick Leave") color = "#8b5cf6";
    if (nextLeave.leave_type === "Emergency Leave") color = "#f97316";
    const returnDate = new Date(
      new Date(nextLeave.end_date).setDate(
        new Date(nextLeave.end_date).getDate() + 1
      )
    );
    return {
      id: nextLeave.id,
      label: `UPCOMING ${nextLeave.leave_type.toUpperCase()}`,
      color,
      start: nextLeave.start_date,
      end: nextLeave.end_date,
      returnDate,
    };
  }, [data, currentStatusInfo, toSQLDate]);

  const leaveBalances = useMemo(() => {
    const aU = data?.profile?.annual_leave_used || 0;
    const aB = data?.profile?.annual_leave_balance || 30;
    return {
      annual: { used: aU, remaining: aB, total: aU + aB, color: "#3b82f6" },
      sick: { used: data?.profile?.sick_leave_count || 0, color: "#8b5cf6" },
      emergency: {
        used: data?.profile?.emergency_leave_count || 0,
        color: "#f97316",
      },
    };
  }, [data]);

  const handleDeleteAuditLog = async (logId) => {
    if (!isAdmin) return;
    try {
      await supabase.from("employee_logs").delete().eq("id", logId);
      startTransition(() =>
        setData((prev) => ({
          ...(prev || {}),
          activityLogs: (prev?.activityLogs || []).filter(
            (l) => l.id !== logId
          ),
        }))
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmitLeave = async () => {
    if (!leaveRequest.startDate || !leaveRequest.endDate)
      return alert("❌ Select Dates!");
    if (!leaveRequest.reason.trim()) return alert("❌ Write a reason!");

    setSubmitting(true);
    try {
      const start = toSQLDate(leaveRequest.startDate);
      const end = toSQLDate(leaveRequest.endDate);
      const days = Math.floor((new Date(end) - new Date(start)) / 86400000) + 1;

      let publicUrl = null;
      if (leaveRequest.file) {
        const fileName = `${userId}/L_${Date.now()}.${leaveRequest.file.name
          .split(".")
          .pop()}`;
        await supabase.storage
          .from("contracts")
          .upload(fileName, leaveRequest.file);
        publicUrl = supabase.storage.from("contracts").getPublicUrl(fileName)
          .data.publicUrl;
      }

      await supabase.from("employee_leaves").insert([
        {
          employee_id: userId,
          leave_type: leaveRequest.leaveType,
          reason: leaveRequest.reason,
          start_date: start,
          end_date: end,
          days_count: days,
          status: "pending",
          document_url: publicUrl,
        },
      ]);

      await supabase.from("employee_logs").insert([
        {
          employee_id: userId,
          action_type: "REQUEST",
          details: `New ${leaveRequest.leaveType} requested: ${formatDate(
            start
          )} to ${formatDate(end)} | Reason: ${leaveRequest.reason}`,
        },
      ]);

      setLeaveRequest({
        leaveType: "Annual Leave",
        reason: "",
        startDate: null,
        endDate: null,
        file: null,
      });
      await refreshData(userId);
      alert("✅ Request Submitted!");
    } catch (e) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ✨ Approve with conditional balance deduction
  const handleApprove = async (req) => {
    const canApprove =
      data?.profile?.role === "admin" ||
      data?.profile?.permissions?.can_access_leave_requests === true;
    if (!canApprove) return;
    setProcessingId(req.id);
    try {
      const { data: emp } = await supabase
        .from("employees")
        .select(
          "annual_leave_balance, annual_leave_used, sick_leave_count, emergency_leave_count"
        )
        .eq("id", req.employee_id)
        .single();

      const updates = {};
      // Deduct balance unconditionally on approval (standard practice)
      if (req.leave_type === "Annual Leave") {
        updates.annual_leave_used =
          (emp.annual_leave_used || 0) + req.days_count;
        updates.annual_leave_balance =
          (emp.annual_leave_balance || 30) - req.days_count;
      } else if (req.leave_type === "Sick Leave") {
        updates.sick_leave_count =
          (emp.sick_leave_count || 0) + req.days_count;
      } else if (req.leave_type === "Emergency Leave") {
        updates.emergency_leave_count =
          (emp.emergency_leave_count || 0) + req.days_count;
      }

      // Calculate return date and update status
      const returnDate = new Date(req.end_date);
      returnDate.setDate(returnDate.getDate() + 1);
      const toSQL = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };

      updates.status = req.leave_type;
      updates.is_on_leave = true;
      updates.last_leave_end_date = toSQL(returnDate);

      if (Object.keys(updates).length > 0) {
        await supabase
          .from("employees")
          .update(updates)
          .eq("id", req.employee_id);
      }

      await supabase
        .from("employee_leaves")
        .update({
          status: "approved",
          approved_by: data?.profile?.full_name,
          approver_number: data?.profile?.employee_number,
          approved_at: new Date().toISOString(),
        })
        .eq("id", req.id);

      await supabase.from("employee_logs").insert([
        {
          employee_id: req.employee_id,
          action_type: "LEAVE_APPROVED",
          details: `Approved: ${data?.profile?.full_name} • #${data?.profile?.employee_number} (Balance deducted)`,
        },
      ]);

      await supabase.from("notifications").insert([
        {
          recipient_id: req.employee_id,
          type: "leave_approved",
          title: "AUTHORIZED",
          message: `Your ${req.leave_type} approved.`,
        },
      ]);

      await refreshData(userId);
      alert("✅ Leave Approved!");
    } catch (e) {
      alert("Error");
    } finally {
      setProcessingId(null);
    }
  };

  // ✨ NEW: Employee/Admin can cancel leave with balance restoration
  const handleCancelLeave = async (req) => {
    if (!window.confirm("⚠️ Cancel this leave request?")) return;

    setProcessingId(req.id);
    try {
      // Restore balance unconditionally if it was approved (since we now deduct unconditionally)
      if (req.status === "approved") {
        const { data: emp } = await supabase
          .from("employees")
          .select(
            "annual_leave_balance, annual_leave_used, sick_leave_count, emergency_leave_count"
          )
          .eq("id", req.employee_id)
          .single();

        const updates = {};
        if (req.leave_type === "Annual Leave") {
          updates.annual_leave_used = Math.max(
            0,
            (emp.annual_leave_used || 0) - req.days_count
          );
          updates.annual_leave_balance =
            (emp.annual_leave_balance || 0) + req.days_count;
        } else if (req.leave_type === "Sick Leave") {
          updates.sick_leave_count = Math.max(
            0,
            (emp.sick_leave_count || 0) - req.days_count
          );
        } else if (req.leave_type === "Emergency Leave") {
          updates.emergency_leave_count = Math.max(
            0,
            (emp.emergency_leave_count || 0) - req.days_count
          );
        }

        // رجّع الموظف Active دائماً عند الكانسل
        updates.status = "Active";
        updates.is_on_leave = false;

        await supabase
          .from("employees")
          .update(updates)
          .eq("id", req.employee_id);
      } else {
        // حتى لو مش approved، رجّع Active
        await supabase
          .from("employees")
          .update({ status: "Active", is_on_leave: false })
          .eq("id", req.employee_id);
      }

      await supabase
        .from("employee_leaves")
        .update({
          status: "cancelled",
          approved_by: data?.profile?.full_name,
          approver_number: data?.profile?.employee_number,
          approved_at: new Date().toISOString(),
        })
        .eq("id", req.id);

      await supabase.from("employee_logs").insert([
        {
          employee_id: req.employee_id,
          action_type: "CANCELLED",
          details: `Cancelled: ${data?.profile?.full_name} • #${data?.profile?.employee_number} (Balance restored)`,
        },
      ]);

      await refreshData(userId);
      alert("✅ Leave Cancelled");
    } catch (e) {
      alert("❌ Cancellation Error");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) return;
    setProcessingId(selectedRequest.id);
    try {
      // رجّع الموظف Active عند الرجكت
      await supabase
        .from("employees")
        .update({ status: "Active", is_on_leave: false })
        .eq("id", selectedRequest.employee_id);

      await supabase
        .from("employee_leaves")
        .update({
          status: "rejected",
          rejection_reason: rejectionReason,
          approved_by: data?.profile?.full_name,
          approver_number: data?.profile?.employee_number,
          approved_at: new Date().toISOString(),
        })
        .eq("id", selectedRequest.id);

      await supabase.from("employee_logs").insert([
        {
          employee_id: selectedRequest.employee_id,
          action_type: "REJECTED",
          details: `Rejected: ${data?.profile?.full_name} • #${data?.profile?.employee_number}. Reason: ${rejectionReason}`,
        },
      ]);

      setShowRejectModal(false);
      setRejectionReason("");
      await refreshData(userId);
      alert("✅ Leave Rejected!");
    } catch (e) {
      alert("Error");
    } finally {
      setProcessingId(null);
    }
  };

  const handleDeleteLeave = async (leaveId) => {
    if (!isAdmin || !window.confirm("⚠️ Permanently delete this record?"))
      return;
    await supabase.from("employee_leaves").delete().eq("id", leaveId);
    await refreshData(userId);
  };

  const printLeaveHistory = () => {
    const managerName = data?.profile?.full_name || "";
    const managerNumber = data?.profile?.employee_number || "";
    const docId = `LH-${data?.profile?.employee_number || "N/A"}-${new Date()
      .toISOString()
      .replace(/[-:T.Z]/g, "")
      .slice(0, 12)}`;
    const today = new Date();
    const preparedDate = `${String(today.getDate()).padStart(2, "0")}/${String(
      today.getMonth() + 1
    ).padStart(2, "0")}/${today.getFullYear()} ${String(
      today.getHours()
    ).padStart(2, "0")}:${String(today.getMinutes()).padStart(2, "0")}`;
    const rows = (data?.leaves || [])
      .map((l) => {
        const start = formatDate(l.start_date);
        const end = formatDate(l.end_date);
        const backDate = formatDate(
          new Date(
            new Date(l.end_date).setDate(new Date(l.end_date).getDate() + 1)
          )
        );
        const manager = `${l.approved_by || ""}${
          l.approver_number ? " • #" + l.approver_number : ""
        }`;
        const doc = l.document_url
          ? `<a href="${l.document_url}" target="_blank" rel="noopener">View Doc</a>`
          : "—";
        return `<tr><td>${l.leave_type}</td><td>${
          l.reason || "—"
        }</td><td>${start} - ${end} (${
          l.days_count || 0
        }D)</td><td>${backDate}</td><td>${
          l.status
        }</td><td>${manager}</td><td>${doc}</td></tr>`;
      })
      .join("");
    const html = `
      <html>
        <head>
          <title>Leave History</title>
          <style>
            @page { size: A4; margin: 14mm; }
            * { box-sizing: border-box; }
            body { font-family: Segoe UI, Tahoma, Arial, sans-serif; color: #0b0b0b; }
            .watermark { position: fixed; top: 40%; left: 50%; transform: translate(-50%, -50%) rotate(-18deg); color: #111; opacity: 0.06; font-size: 110px; font-weight: 900; pointer-events: none; }
            .header { display: grid; grid-template-columns: 1fr auto; gap: 16px; align-items: center; border-bottom: 2px solid #111; padding-bottom: 10px; }
            .brand-title { font-size: 20px; font-weight: 900; letter-spacing: 1px; }
            .brand-sub { font-size: 11px; color: #666; }
            .meta-box { display: grid; grid-template-columns: auto auto; gap: 6px 12px; font-size: 11px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; font-size: 12px; }
            th { background: #f7f7f7; text-transform: uppercase; }
            .section-title { margin: 18px 0 8px; font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; }
            .balances th, .balances td { text-align: center; }
            .signature { margin-top: 22px; display: grid; grid-template-columns: 2fr 1fr; gap: 16px; align-items: end; }
            .line { border-bottom: 1px solid #222; height: 26px; }
            .small { font-size: 11px; color: #666; }
            .footer { margin-top: 22px; border-top: 1px solid #ddd; padding-top: 10px; font-size: 10px; color: #555; display: grid; grid-template-columns: 1fr auto; gap: 10px; align-items: center; }
            .rights { font-weight: 600; }
            .stamp { border: 1px dashed #999; height: 60px; border-radius: 8px; text-align: center; font-size: 10px; color: #999; display: flex; align-items: center; justify-content: center; }
          </style>
        </head>
        <body>
          <div class="watermark">CORE Professional Consulting</div>
          <div class="header">
            <div>
              <div class="brand-title">CORE Professional Consulting</div>
              <div class="brand-sub">Employee Leave History Report</div>
            </div>
            <div style="display:flex; gap:12px; align-items:center;">
              <div class="meta-box">
                <div><b>Document ID:</b></div><div>${docId}</div>
                <div><b>Prepared By:</b></div><div>${managerName || "—"}${
      managerNumber ? " • #" + managerNumber : ""
    }</div>
                <div><b>Prepared On:</b></div><div>${preparedDate}</div>
              </div>
            </div>
          </div>

          <div class="section-title">Employee Summary</div>
          <table>
            <tbody>
              <tr><th>Full Name</th><td>${
                data?.profile?.full_name || "—"
              }</td><th>Staff ID</th><td>${
      data?.profile?.employee_number || "—"
    }</td></tr>
              <tr><th>Job Title</th><td>${
                data?.profile?.job_title || "—"
              }</td><th>Department</th><td>${
      data?.profile?.department || "—"
    }</td></tr>
              <tr><th>National ID</th><td>${
                data?.profile?.national_id || "—"
              }</td><th>Status</th><td>${data?.profile?.status || "—"}</td></tr>
              <tr><th>Phone</th><td>${
                data?.profile?.phone_number || "—"
              }</td><th>Email</th><td>${data?.profile?.email || "—"}</td></tr>
            </tbody>
          </table>

          <div class="section-title">Leave History</div>
          <table>
            <thead><tr><th>Type</th><th>Reason</th><th>Period</th><th>Back</th><th>Status</th><th>Manager</th><th>Doc</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>

          <div class="signature">
            <div>
              <div class="small">Manager: ${managerName || "—"}${
      managerNumber ? " • #" + managerNumber : ""
    }</div>
              <div class="line"></div>
              <div class="small">Manager Signature</div>
            </div>
            <div>
              <div class="small">Date</div>
              <div class="line"></div>
            </div>
          </div>

          <div class="footer">
            <div>
              <div class="rights">© ${new Date().getFullYear()} CORE Professional Consulting. جميع الحقوق محفوظة.</div>
              <div class="small">هذا المستند سري ومخصص للاستخدام الداخلي فقط.</div>
            </div>
            <div class="stamp">Official Company Stamp</div>
          </div>
          <script>window.onload = () => window.print();</script>
        </body>
      </html>
    `;
    const w = window.open("", "_blank");
    if (w) {
      w.document.write(html);
      w.document.close();
    }
  };

  const clearLeaveHistory = async () => {
    if (!isAdmin || !window.confirm("⚠️ Wipe all leave history?")) return;
    await supabase.from("employee_leaves").delete().eq("employee_id", userId);
    await refreshData(userId);
  };

  const clearAuditTrail = async () => {
    if (!isAdmin || !window.confirm("⚠️ Wipe all audit logs?")) return;
    await supabase.from("employee_logs").delete().eq("employee_id", userId);
    await refreshData(userId);
  };

  if (loading)
    return (
      <div className="flex h-screen w-full animate-pulse items-center justify-center bg-white font-mono text-xs uppercase text-[#0055FF]">
        SYNCHRONIZING_CORE_DATA_V6...
      </div>
    );

  return (
    <div
      className="animate-fadeIn mt-3 w-full space-y-6 p-2 text-left md:p-0"
      dir="ltr"
    >
      {/* 1. Widgets */}
      <div className="grid grid-cols-1 gap-5 font-mono md:grid-cols-2 lg:grid-cols-5">
        <Widget
          icon={<MdVerifiedUser className="text-emerald-500" />}
          title="Contract Status"
          subtitle="ACTIVE"
          extra={cardStyle}
        />
        <Widget
          icon={<MdTimer className="text-blue-400" />}
          title="Leave Requests"
          subtitle={isAdmin ? 0 : data?.leaves?.length || 0}
          extra={cardStyle}
        />
        
        <Widget
          icon={<MdAssignmentInd className="text-brand-500" />}
          title="Staff ID"
          subtitle={data?.profile?.employee_number}
          extra={cardStyle}
        />
        <Widget
          icon={<MdAccessTime className="text-orange-400" />}
          title="Used Balance"
          subtitle={`${leaveBalances.annual.used}D`}
          extra={cardStyle}
        />
        <Widget
          icon={<MdNotifications className="text-purple-400" />}
          title="Alerts"
          subtitle={data?.unread}
          extra={cardStyle}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 print:grid-cols-1">
        <div className="space-y-6 lg:col-span-2">
          {/* 2. Profile Card */}
          <Card extra={`p-8 ${cardStyle}`}>
            <div className="mb-8 flex flex-col items-start justify-between border-b border-gray-200 pb-6 md:flex-row md:items-center">
              <div className="flex items-center gap-6">
                <SafeAvatar
                  url={data?.profile?.avatar_url}
                  wrapperClassName="h-24 w-24"
                  imageClassName="h-full w-full rounded-3xl border-2 border-core-accent bg-white p-1"
                />
                <div>
                  <h4 className="font-mono text-3xl font-medium uppercase italic text-core-charcoal">
                    {data?.profile?.full_name}
                  </h4>
                  <div className="mt-3 flex gap-2">
                    <span className="rounded bg-core-accent px-2 py-1 text-[10px] font-medium uppercase tracking-widest text-white">
                      {data?.profile?.job_title}
                    </span>
                    <span className="rounded bg-gray-100 px-2 py-1 text-[10px] font-medium uppercase text-gray-600">
                      {data?.profile?.department}
                    </span>
                  </div>

                  {currentStatusInfo.isOnLeave && data?.profile?.last_leave_end_date && (
                    <div className="mt-4 flex w-fit items-center gap-2 rounded-xl border border-[#f97316]/20 bg-[#f97316]/10 px-4 py-2 text-[#f97316] shadow-[0_0_10px_rgba(249,115,22,0.1)]">
                      <MdCalendarToday size={18} />
                      <span className="font-mono text-[11px] font-medium uppercase tracking-tighter">
                        RETURN: {formatDate(data.profile.last_leave_end_date)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div
                  style={{
                    backgroundColor: upcomingStatusInfo
                      ? upcomingStatusInfo.color
                      : currentStatusInfo.bg,
                  }}
                  className="rounded-2xl border border-gray-200 px-8 py-3 text-[12px] font-medium uppercase tracking-widest text-white shadow-lg"
                >
                  {upcomingStatusInfo
                    ? upcomingStatusInfo.label
                    : currentStatusInfo.status}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <InfoField
                icon={<MdAssignmentInd />}
                label="Full Name"
                value={data?.profile?.full_name}
              />
              <InfoField
                icon={<MdWork />}
                label="Job Title"
                value={data?.profile?.job_title}
              />
              <InfoField
                icon={<MdAccountTree />}
                label="Department"
                value={data?.profile?.department}
              />
              <InfoField
                icon={<MdFingerprint />}
                label="National ID"
                value={data?.profile?.national_id}
              />
              <InfoField
                icon={<MdWork />}
                label="Staff ID"
                value={data?.profile?.employee_number}
              />
              <InfoField
                icon={<MdContactPhone />}
                label="Phone Number"
                value={data?.profile?.phone_number}
              />
              <InfoField
                icon={<MdEmail />}
                label="Email Address"
                value={data?.profile?.email}
              />
              <InfoField
                icon={<MdDateRange />}
                label="Contract Start"
                value={formatDate(data?.profile?.contract_start)}
              />
              <InfoField
                icon={<MdDateRange />}
                label="Contract End"
                value={formatDate(data?.profile?.contract_end)}
              />
            </div>
          </Card>

          {/* 3. Leave History */}
          <Card extra={`p-6 ${cardStyle}`}>
            <div className="mb-6 flex items-center justify-between">
              <h3 className="flex items-center gap-3 font-mono text-sm font-medium uppercase italic text-core-charcoal">
                <MdHistory className="text-core-accent" size={24} /> LEAVE HISTORY
              </h3>
              <div className="flex items-center gap-2">
                {data?.profile?.role === "admin" && (
                  <>
                    <button
                      onClick={printLeaveHistory}
                      className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-[10px] font-medium text-core-charcoal transition-all hover:bg-gray-100"
                    >
                      PRINT
                    </button>
                    <button
                      onClick={clearLeaveHistory}
                      className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-1.5 text-[10px] font-medium text-red-500 transition-all hover:bg-red-500 hover:text-white"
                    >
                      <MdDeleteSweep size={16} /> CLEAR ALL
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left font-mono text-xs">
                <thead>
                  <tr className="border-b border-gray-200 text-[10px] uppercase text-gray-500">
                    <th className="px-2 pb-3">TYPE</th>
                    <th className="px-2 pb-3">REASON</th>
                    <th className="px-2 pb-3">PERIOD</th>
                    <th className="px-2 pb-3 text-right">STATUS</th>
                    <th className="px-2 pb-3 text-right">DOC</th>
                    <th className="px-2 pb-3 text-right">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.leaves?.map((l) => (
                    <tr
                      key={l.id}
                      className="border-b border-gray-200 transition-colors hover:bg-gray-50"
                    >
                      <td className="px-2 py-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{
                                backgroundColor:
                                  leaveTypeColors[l.leave_type]?.dot,
                              }}
                            ></span>
                            <span
                              className={`font-medium uppercase ${
                                leaveTypeColors[l.leave_type]?.text
                              } ${
                                leaveTypeColors[l.leave_type]?.bg
                              } rounded-lg border border-gray-200 px-2 py-0.5`}
                            >
                              {l.leave_type}
                            </span>
                          </div>
                          <div className="ml-4 flex items-center gap-1.5 text-[9px] font-medium text-[#f97316]">
                            <MdDateRange size={12} /> BACK:{" "}
                            {formatDate(
                              new Date(
                                new Date(l.end_date).setDate(
                                  new Date(l.end_date).getDate() + 1
                                )
                              )
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="max-w-[150px] truncate px-2 py-4 text-[10px] text-gray-400">
                        {l.reason || "---"}
                      </td>
                      <td className="px-2 py-4 text-gray-400">
                        {formatDate(l.start_date)} - {formatDate(l.end_date)} (
                        {l.days_count}D)
                      </td>
                      <td className="px-2 py-4 text-right">
                        <div className="flex flex-col items-end gap-2">
                          <span
                            className={`text-[10px] font-medium uppercase ${
                              l.status === "approved"
                                ? "text-emerald-500"
                                : l.status === "rejected"
                                ? "text-red-500"
                                : l.status === "cancelled"
                                ? "text-gray-500"
                                : "text-yellow-500"
                            }`}
                          >
                            {l.status}
                          </span>
                          {l.status !== "pending" && (
                            <span className="text-[9px] uppercase text-gray-500">
                              Manager: {l.approved_by || "---"}
                              {l.approver_number
                                ? ` • #${l.approver_number}`
                                : ""}
                            </span>
                          )}

                          {l.status === "rejected" && (
                            <div className="w-full min-w-[260px] rounded-xl border-2 border-red-500/30 bg-red-500/10 p-4 shadow-2xl">
                              <div className="mb-3 flex items-center gap-2 border-b border-red-500/20 pb-2">
                                <MdErrorOutline
                                  className="text-red-500"
                                  size={18}
                                />
                                <div className="text-left">
                                  <p className="mb-1 text-[9px] font-medium uppercase leading-none tracking-widest text-gray-400">
                                    Rejected By
                                  </p>
                                  <p className="text-[11px] font-medium uppercase text-core-charcoal">
                                    {l.approved_by || "ADMINISTRATOR"}
                                    {l.approver_number
                                      ? ` • #${l.approver_number}`
                                      : ""}
                                  </p>
                                </div>
                              </div>
                              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                <p className="text-red-600 text-left text-[11px] font-medium italic leading-relaxed">
                                  {l.rejection_reason || "—"}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-4 text-right">
                        {l.document_url ? (
                          <a
                            href={l.document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1 text-[9px] font-medium text-core-charcoal hover:bg-gray-100"
                          >
                            <MdAttachment size={12} /> VIEW
                          </a>
                        ) : (
                          <span className="text-[9px] text-gray-500">—</span>
                        )}
                      </td>

                      <td className="px-2 py-4 text-right">
                        <div className="flex justify-end gap-1.5">
                          {/* Admin-only buttons */}
                          {(data?.profile?.role === "admin" ||
                            data?.profile?.permissions
                              ?.can_access_leave_requests) &&
                            l.status === "pending" && (
                              <>
                                <button
                                  onClick={() => handleApprove(l)}
                                  disabled={processingId === l.id}
                                  className="rounded-md border border-green-500/20 bg-green-500/10 p-1.5 text-green-500 transition-all hover:bg-green-500 hover:text-white disabled:opacity-50"
                                  title="Approve"
                                >
                                  <MdCheckCircle size={14} />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedRequest(l);
                                    setShowRejectModal(true);
                                  }}
                                  className="rounded-md border border-red-500/20 bg-red-500/10 p-1.5 text-red-500 transition-all hover:bg-red-500 hover:text-white"
                                  title="Reject"
                                >
                                  <MdCancel size={14} />
                                </button>
                              </>
                            )}

                          {/* Cancel button للإداري فقط */}
                          {isAdmin &&
                            (l.status === "approved" ||
                              l.status === "pending") && (
                              <button
                                onClick={() => handleCancelLeave(l)}
                                disabled={processingId === l.id}
                                className="bg-orange-500/10 text-orange-500 border-orange-500/20 hover:bg-orange-500 rounded-md border p-1.5 transition-all hover:text-white disabled:opacity-50"
                                title="Cancel Leave"
                              >
                                <MdCancel size={14} />
                              </button>
                            )}

                          {/* Admin-only delete */}
                          {data?.profile?.role === "admin" && (
                            <button
                              onClick={() => handleDeleteLeave(l.id)}
                              className="rounded-md border border-gray-500/20 bg-gray-500/10 p-1.5 text-gray-500 transition-all hover:bg-gray-500 hover:text-white"
                              title="Permanent Delete"
                            >
                              <MdDelete size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* 4. Audit Trail */}
          <Card extra={`p-6 ${cardStyle}`}>
            <div className="mb-6 flex items-center justify-between">
              <h3 className="flex items-center gap-3 font-mono text-sm font-medium uppercase italic text-core-charcoal">
                <MdTerminal className="text-emerald-500" size={24} /> AUDIT
                TRAIL
              </h3>
              {data?.profile?.role === "admin" && (
                <button
                  onClick={clearAuditTrail}
                  className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-1.5 text-[10px] font-medium text-red-500 transition-all hover:bg-red-500 hover:text-white"
                >
                  <MdDeleteSweep size={16} /> CLEAR ALL
                </button>
              )}
            </div>

            <div className="custom-scrollbar max-h-[300px] space-y-4 overflow-y-auto pr-2 font-mono">
              {data?.activityLogs?.map((log) => (
                <div
                  key={log.id}
                  className="group rounded-xl border-b border-gray-200 bg-gray-50 p-4"
                >
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-emerald-500 bg-emerald-500/10 rounded px-2 py-1 text-[9px] font-medium uppercase">
                      {log.action_type}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-[8px] text-gray-600">
                        {new Date(log.created_at).toLocaleString("en-GB")}
                      </span>
                      {isAdmin && (
                        <button
                          onClick={() => handleDeleteAuditLog(log.id)}
                          className="text-red-500 opacity-0 transition-all group-hover:opacity-100"
                          title="Delete log"
                        >
                          <MdDelete size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                  <pre className="whitespace-pre-line text-[10px] italic leading-relaxed text-gray-500">
                    {log.details}
                  </pre>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6 print:hidden">
          {/* ✨ NEW: Unified ACTIVE LEAVE Card (current or upcoming) */}
          {(currentStatusInfo.isOnLeave || upcomingStatusInfo) && (
            <Card
              extra={`p-6 bg-white border-2 shadow-2xl`}
              style={{
                borderColor: currentStatusInfo.isOnLeave
                  ? currentStatusInfo.bg
                  : upcomingStatusInfo?.color || "#3b82f6",
              }}
            >
              <h3
                className="mb-4 flex items-center gap-3 font-mono text-[10px] font-medium uppercase"
                style={{
                  color: currentStatusInfo.isOnLeave
                    ? currentStatusInfo.bg
                    : upcomingStatusInfo?.color || "#3b82f6",
                }}
              >
                <MdCancel size={18} /> ACTIVE LEAVE
              </h3>
              <div className="space-y-3">
                <div className="text-center">
                  <p className="mb-1 text-[9px] uppercase text-gray-400">
                    Currently On
                  </p>
                  <p
                    className="text-sm font-medium uppercase"
                    style={{
                      color: currentStatusInfo.isOnLeave
                        ? currentStatusInfo.bg
                        : upcomingStatusInfo?.color || "#3b82f6",
                    }}
                  >
                    {currentStatusInfo.isOnLeave
                      ? currentStatusInfo.status
                      : upcomingStatusInfo?.label || "UPCOMING"}
                  </p>
                  <p className="text-orange-400 mt-2 text-[10px]">
                    Return:{" "}
                    {formatDate(
                      currentStatusInfo.isOnLeave
                        ? currentStatusInfo.returnDate
                        : upcomingStatusInfo?.returnDate || new Date()
                    )}
                  </p>
                </div>
                {isAdmin && currentStatusInfo.isOnLeave && (
                  <button
                    onClick={() => {
                      const activeLeave = data.leaves.find(
                        (l) => l.id === currentStatusInfo.leaveId
                      );
                      if (activeLeave) handleCancelLeave(activeLeave);
                    }}
                    disabled={processingId !== null}
                    className="bg-orange-500 hover:bg-orange-600 w-full rounded-xl py-3 text-[10px] font-medium uppercase text-white shadow-lg transition-all disabled:opacity-50"
                  >
                    CANCEL LEAVE
                  </button>
                )}
                {isAdmin &&
                  !currentStatusInfo.isOnLeave &&
                  upcomingStatusInfo && (
                    <button
                      onClick={() => {
                        const nextLeave = data.leaves.find(
                          (l) => l.id === upcomingStatusInfo.id
                        );
                        if (nextLeave) handleCancelLeave(nextLeave);
                      }}
                      disabled={processingId !== null}
                      className="bg-orange-500 hover:bg-orange-600 w-full rounded-xl py-3 text-[10px] font-medium uppercase text-white shadow-lg transition-all disabled:opacity-50"
                    >
                      CANCEL LEAVE
                    </button>
                  )}
              </div>
            </Card>
          )}

          <Card extra={`p-8 bg-white border border-gray-200 shadow-sm`}>
            <h3 className="mb-6 flex items-center gap-3 font-mono text-[10px] font-medium uppercase text-core-charcoal">
              <MdFileUpload className="text-core-accent" size={20} /> NEW REQUEST
            </h3>

            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-2">
                {["Annual Leave", "Sick Leave", "Emergency Leave"].map(
                  (type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() =>
                        setLeaveRequest({ ...leaveRequest, leaveType: type })
                      }
                      className={`rounded-lg border px-4 py-2.5 text-[10px] font-medium uppercase transition-all ${
                        leaveRequest.leaveType === type
                          ? "border-core-accent bg-core-accent text-white shadow-md"
                          : "border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100"
                      }`}
                    >
                      {type}
                    </button>
                  )
                )}
              </div>

              <input
                type="text"
                className={inputStyle}
                placeholder="REASON..."
                value={leaveRequest.reason}
                onChange={(e) =>
                  setLeaveRequest({ ...leaveRequest, reason: e.target.value })
                }
              />

              <div className="grid grid-cols-2 gap-4">
                <Flatpickr
                  value={leaveRequest.startDate}
                  onChange={([d]) =>
                    setLeaveRequest({ ...leaveRequest, startDate: d })
                  }
                  options={{
                            dateFormat: "d/m/Y",
                            disableMobile: true,
                          }}
                  className={inputStyle}
                  placeholder="FROM"
                />
                <Flatpickr
                  value={leaveRequest.endDate}
                  onChange={([d]) =>
                    setLeaveRequest({ ...leaveRequest, endDate: d })
                  }
                  options={{
                    dateFormat: "d/m/Y",
                    minDate: leaveRequest.startDate || "today",
                    disableMobile: true,
                  }}
                  className={inputStyle}
                  placeholder="TO"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="file"
                  id="file-up"
                  className="hidden"
                  onChange={(e) =>
                    setLeaveRequest({
                      ...leaveRequest,
                      file: e.target.files[0],
                    })
                  }
                />
                <button
                  type="button"
                  onClick={() => document.getElementById("file-up")?.click()}
                  className="rounded-lg bg-core-accent px-4 py-2 text-[10px] font-medium text-white transition-all hover:opacity-90"
                >
                  ATTACH FILE
                </button>
                <span className="truncate text-[10px] text-gray-500">
                  {leaveRequest.file?.name || "OPTIONAL"}
                </span>
              </div>

              <button
                onClick={handleSubmitLeave}
                disabled={
                  submitting ||
                  !leaveRequest.reason.trim() ||
                  !leaveRequest.startDate ||
                  !leaveRequest.endDate
                }
                className={`w-full rounded-xl bg-core-accent py-4 text-xs font-medium uppercase text-white shadow-lg transition-all ${
                  submitting ||
                  !leaveRequest.reason.trim() ||
                  !leaveRequest.startDate ||
                  !leaveRequest.endDate
                    ? "cursor-not-allowed opacity-50"
                    : "hover:scale-[1.02]"
                }`}
              >
                {submitting ? "PROCESSING..." : "SUBMIT REQUEST"}
              </button>
            </div>
          </Card>

          {/* Attachments Section */}
          <Card extra={`p-6 bg-white border border-gray-200 shadow-sm`}>
            <h3 className="mb-4 flex items-center gap-3 font-mono text-[10px] font-medium uppercase text-core-charcoal">
              <MdAttachment className="text-core-accent" size={18} /> DIGITAL
              VAULT
            </h3>

            <div className="space-y-3">
              {(() => {
                const assets = Array.isArray(data?.profile?.contract_assets)
                  ? data.profile.contract_assets
                  : [];
                const primaryUrl =
                  data?.profile?.contract_pdf_url || (assets[0]?.url ?? null);
                const others = assets.filter((a) => a.url !== primaryUrl);

                const deleteMainContract = async () => {
                  if (!window.confirm("Delete main contract file?")) return;
                  await supabase.from("employees").update({
                    contract_pdf_url: null,
                    contract_assets: assets.filter(a => a.url !== primaryUrl),
                  }).eq("id", data.profile.id);
                  await refreshData(userId);
                };

                const deleteAsset = async (fileUrl) => {
                  if (!window.confirm("Delete this file?")) return;
                  const newAssets = assets.filter(a => a.url !== fileUrl);
                  await supabase.from("employees").update({
                    contract_assets: newAssets,
                    ...(data?.profile?.contract_pdf_url === fileUrl ? { contract_pdf_url: null } : {}),
                  }).eq("id", data.profile.id);
                  await refreshData(userId);
                };

                return (
                  <>
                    {primaryUrl && (
                      <div className="group flex items-center justify-between rounded-lg border border-core-accent/20 bg-core-accent/5 p-3 transition-all hover:bg-core-accent/10">
                        <a href={primaryUrl} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-3 flex-1">
                          <MdDescription className="text-core-accent" size={20} />
                          <span className="font-mono text-[10px] font-medium uppercase text-core-charcoal">
                            Main Contract
                          </span>
                          <MdOpenInNew className="text-gray-400 group-hover:text-core-accent" size={14} />
                        </a>
                        {isAdmin && (
                          <button onClick={deleteMainContract}
                            className="ml-2 rounded-md p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600 transition-all"
                            title="Delete file">
                            <MdDelete size={16} />
                          </button>
                        )}
                      </div>
                    )}
                    {others.map((file, idx) => (
                      <div key={idx}
                        className="group flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3 transition-all hover:border-core-accent/30">
                        <a href={file.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-3 flex-1">
                          <MdDescription className="text-gray-500" size={20} />
                          <span className="max-w-[140px] truncate font-mono text-[10px] font-medium uppercase text-gray-600">
                            {file.name || `Asset ${idx + 1}`}
                          </span>
                          <MdOpenInNew className="text-gray-400" size={14} />
                        </a>
                        {isAdmin && (
                          <button onClick={() => deleteAsset(file.url)}
                            className="ml-2 rounded-md p-1.5 text-red-400 hover:bg-red-50 hover:text-red-600 transition-all"
                            title="Delete file">
                            <MdDelete size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </>
                );
              })()}

              {data?.leaves
                ?.filter((l) => l.document_url)
                .map((leave) => (
                  <a
                    key={leave.id}
                    href={leave.document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3 transition-all hover:border-core-accent/30"
                  >
                    <span className="text-[9px] font-medium uppercase leading-none text-gray-600">
                      {leave.leave_type} Doc
                    </span>
                    <MdOpenInNew className="text-gray-600" size={12} />
                  </a>
                ))}

              {!data?.profile?.contract_assets?.length &&
                !data?.profile?.contract_pdf_url && (
                  <p className="py-4 text-center font-mono text-[10px] italic text-gray-400">
                    No documents found
                  </p>
                )}
            </div>
          </Card>

          <Card extra={`p-6 bg-white border border-gray-200 shadow-sm`}>
            <h3 className="mb-4 flex items-center gap-3 font-mono text-[9px] font-medium uppercase text-core-charcoal">
              <MdInfo className="text-core-accent" size={16} /> BALANCES
            </h3>
            <div className="space-y-2">
              <BalanceRow
                label="ANNUAL"
                used={leaveBalances.annual.used}
                total={leaveBalances.annual.total}
                remaining={leaveBalances.annual.remaining}
                color="#3b82f6"
              />
              <BalanceRow
                label="SICK"
                used={leaveBalances.sick.used}
                color="#8b5cf6"
              />
              <BalanceRow
                label="EMERGENCY"
                used={leaveBalances.emergency.used}
                color="#f97316"
              />
            </div>
          </Card>
        </div>
      </div>

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="bg-core-charcoal/50 fixed inset-0 z-[200] flex items-center justify-center p-4 backdrop-blur-sm">
          <Card
            extra={`p-8 max-w-md w-full bg-white border border-gray-200 shadow-xl`}
          >
            <h3 className="mb-2 font-mono text-sm font-medium uppercase text-core-charcoal">
              REJECT REQUEST
            </h3>
            <textarea
              className="h-32 w-full resize-none rounded-2xl border border-gray-200 bg-gray-50 p-4 font-mono text-xs text-core-charcoal outline-none focus:border-red-500"
              placeholder="REJECTION REASON..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
            <div className="mt-6 flex gap-4">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason("");
                }}
                className="flex-1 rounded-2xl bg-gray-100 py-4 text-[11px] font-medium uppercase text-gray-600 transition-all hover:bg-gray-200"
              >
                CANCEL
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectionReason.trim() || processingId !== null}
                className="hover:bg-red-700 flex-1 rounded-2xl bg-red-600 py-4 text-[11px] font-medium uppercase text-white shadow-lg transition-all disabled:opacity-50"
              >
                REJECT
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Dashboard;